<script lang="ts" setup>
import { inject, ref, reactive, computed, nextTick } from 'vue';
import UpdatePerspective from '@/components/Perspectives/UpdatePerspective.vue';
import CollapseFieldset from '@/components/CollapseFieldset.vue';

import type { Ref } from 'vue';
import type { Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest } from '@/types/IManifest';

const manifest = inject('manifest') as Ref<IManifest>;
const activePerspective = inject('activePerspective') as Ref<Nullable<string>>;
const configurationPanels = reactive(new Map<string, boolean>());
const search = ref('');

const filteredPerspectives = computed(() => {
  if (search.value.length === 0) return Object.keys(manifest.value.views);

  return Object.keys(manifest.value.views).filter((viewName) => {
    if (viewName.toLowerCase().includes(search.value.toLowerCase())) return true;

    return false;
  });
});

const toggleActivePerspective = (newPerspective: string) => {
  if (activePerspective.value === newPerspective) {
    activePerspective.value = null;
  } else {
    activePerspective.value = null;

    nextTick(() => {
      activePerspective.value = newPerspective;
    });
  }
};

const renameView = (oldName: string, newName: string) => {
  configurationPanels.set(newName, configurationPanels.get(oldName) as boolean);
  configurationPanels.delete(oldName);
};
</script>

<template>
  <CollapseFieldset>
    <template #legend>
      Perspective List
    </template>

    <input v-model="search" type="text" placeholder="Search..." class="text-xs border w-full p-1 mb-1">

    <div v-if="filteredPerspectives.length === 0" class="flex flex-col p-1">
      <span class="text-xs">No perspectives<span v-if="search.length > 0" class="text-xs"> found for search "{{ search }}"</span></span>
    </div>

    <div class="max-h-[500px] overflow-y-auto">
      <div v-for="viewName in filteredPerspectives" :key="viewName" class="flex flex-col flex-grow p-1 justify-between border-b last-of-type:border-none" :class="{ 'bg-slate-300': activePerspective === viewName, 'hover:bg-slate-200/75': activePerspective !== viewName }">
        <div class="flex flex-row space-between items-center">
          <span class="text-xs flex-1 block cursor-pointer" @click="() => toggleActivePerspective(viewName)">{{ viewName }}</span>
        </div>

        <UpdatePerspective v-if="configurationPanels.get(viewName) === true" :identifier="viewName" @rename="renameView" />
      </div>
    </div>
  </CollapseFieldset>
</template>
